import { useState, useEffect, useMemo, useRef, RefObject } from 'react';
import { Project, SymbolType, TradeSymbol } from '@shared/types/tsk';
import { GoogleGenAI } from "@google/genai";
import { flattenMarkupToCanvas } from '../utils/drawing';
import { exportReportToPDF } from '../utils/reportExport';
import { updateProject } from '../utils/storage';
import { useAuth } from '@clerk/react';
import { platform } from '@/platform.config';

export function useGeminiPanel(
  project: Project | undefined, 
  onClose: () => void, 
  autoDownload: boolean,
  containerRef: RefObject<HTMLDivElement>,
  exportTemplateRef: RefObject<HTMLDivElement>
) {
  const [status, setStatus] = useState<'analyzing' | 'done' | 'error'>('analyzing');
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [aiStatement, setAiStatement] = useState<string>('');
  const [flattenedPlan, setFlattenedPlan] = useState<string | null>(null);
  const [isFlattening, setIsFlattening] = useState(true);
  const [previewScale, setPreviewScale] = useState(1);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [showBilling, setShowBilling] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { getToken } = useAuth();
  const { image, markup } = project || { image: null, markup: { lines: [], symbols: [] } };

  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const padding = 32;
        const containerWidth = containerRef.current.clientWidth - padding;
        const scale = Math.min(1, containerWidth / (210 * 3.7795));
        setPreviewScale(scale);
      }
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [status, isFlattening, containerRef]);

  const sanitizeText = (text: string) => text.replace(/[#*`_~>]/g, '').replace(/\[.*\]\(.*\)/g, '').replace(/\n+/g, ' ').trim();

  const getFullName = (type: SymbolType): string => {
    switch (type) {
      case SymbolType.WC: return 'Water Closet';
      case SymbolType.BASIN: return 'Basin';
      case SymbolType.SHOWER: return 'Shower';
      case SymbolType.SINK: return 'Sink';
      case SymbolType.BATH: return 'Bath';
      case SymbolType.WASHING_MACHINE: return 'Washing Machine';
      case SymbolType.DISHWASHER: return 'Dishwasher';
      case SymbolType.LAUNDRY_TUB: return 'Laundry Tub';
      case SymbolType.FLOOR_WASTE: return 'Floor Waste';
      case SymbolType.TUNDISH: return 'Tundish';
      case SymbolType.IO: return 'Inspection Opening';
      case SymbolType.IOS: return 'Inspection Opening Surface';
      case SymbolType.CO: return 'Cleaning Opening';
      case SymbolType.COS: return 'Cleaning Opening Surface';
      case SymbolType.ORG: return 'Overflow Relief Gully';
      case SymbolType.SWIVEL_JOINT: return 'Swivel Joint';
      case SymbolType.EXPANSION_JOINT: return 'Expansion Joint';
      case SymbolType.RISER: return 'Riser';
      case SymbolType.DROPPER: return 'Dropper';
      default: return type;
    }
  };

  const fixtureCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const fixtureTypes = [
      SymbolType.WC, SymbolType.BASIN, SymbolType.SHOWER, SymbolType.BATH,
      SymbolType.SINK, SymbolType.WASHING_MACHINE, SymbolType.DISHWASHER,
      SymbolType.LAUNDRY_TUB, SymbolType.FLOOR_WASTE, SymbolType.TUNDISH
    ];
    markup.symbols.filter((s: TradeSymbol) => fixtureTypes.includes(s.type)).forEach((s: TradeSymbol) => {
      const name = getFullName(s.type);
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [markup.symbols]);

  const fittingCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const fittingTypes = [
      SymbolType.IO, SymbolType.IOS, SymbolType.CO, SymbolType.COS,
      SymbolType.ORG, SymbolType.SWIVEL_JOINT, SymbolType.EXPANSION_JOINT,
      SymbolType.RISER, SymbolType.DROPPER
    ];
    markup.symbols.filter((s: TradeSymbol) => fittingTypes.includes(s.type)).forEach((s: TradeSymbol) => {
      const name = getFullName(s.type);
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [markup.symbols]);

  useEffect(() => {
    const flattenPlan = async () => {
      if (!image) return;
      setIsFlattening(true);
      try {
        const canvas = await flattenMarkupToCanvas(image, markup);
        setFlattenedPlan(canvas.toDataURL('image/jpeg', 0.95));
      } catch (err) {
        console.error("Flattening failed", err);
      } finally {
        setIsFlattening(false);
      }
    };

    const generateAiStatement = async () => {
      if (!project) return;
      try {
        const apiKey = process.env.GEMINI_API_KEY;
        if (!apiKey) {
          setAiStatement(`As Constructed Water and Sanitary Drainage Plan for property at ${project.address || 'specified location'}, compliant with AS/NZS 3500 standards.`);
          setStatus('done');
          return;
        }
        const ai = new GoogleGenAI({ apiKey });
        const prompt = `
          Draft a one-sentence professional engineering summary for an "As Constructed Water and Sanitary Drainage Plan".
          Reference Property: ${project.address || 'Unknown'}.
          Mention compliance with AS/NZS 3500 standards.
          Plain text only.
        `;
        const result = await ai.models.generateContent({
          model: 'gemini-1.5-flash',
          contents: [{ role: 'user', parts: [{ text: prompt }] }]
        });
        setAiStatement(sanitizeText(result.text || ""));
        setStatus('done');
      } catch (err) {
        console.error(err);
        setStatus('done');
      }
    };

    flattenPlan().then(generateAiStatement);
  }, [project, image, markup]);

  useEffect(() => {
    const checkAuth = async () => {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocalhost || process.env.NODE_ENV === 'test' || (window as any).isPlaywrightTest) {
        setIsAuthorized(true);
        setIsCheckingAuth(false);
        return;
      }
      if (!project?.id) return;
      setIsCheckingAuth(true);
      const getTokenWithTimeout = async () => {
        if ((window as any).isPlaywrightTest) return 'mock_token_for_test';
        const timeoutPromise = new Promise<null>((_, reject) => setTimeout(() => reject(new Error('Auth Timeout')), 5000));
        try {
          if (typeof getToken === 'function') {
            return await Promise.race([getToken(), timeoutPromise]);
          }
          return null;
        } catch (e) {
          console.warn('Clerk Token Retrieval failed or timed out:', e);
          return null;
        }
      };
      try {
        const token = await getTokenWithTimeout();
        const response = await fetch(`${platform.hubUrl}/api/billing/v1/auth-check`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ projectId: project.id, projectName: project.name }),
        });
        if (!response.ok) throw new Error(`Billing API error: ${response.status}`);
        const data = await response.json();
        if (data.authorized) {
          setIsAuthorized(true);
          if (!project.isLocked) {
             await updateProject(project.id, { ...project, isLocked: true, authorizedAt: Date.now() });
          }
        } else {
          setIsAuthorized(false);
          setShowBilling(true);
        }
      } catch (err) {
        console.error('Auth check failed', err);
        setIsAuthorized(false);
        setShowBilling(true);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [project, getToken]);

  const handlePrint = async () => {
    if (isFlattening || isDownloading) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const safeName = (project?.name || 'Sketch').replace(/[^a-z0-9]/gi, '_');
      await exportReportToPDF(exportTemplateRef.current, `Report_${safeName}`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (project?.id && !isLocalhost) {
        (async () => {
          try {
            const token = await getToken();
            await fetch(`${platform.hubUrl}/api/telemetry/v1/log`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
              },
              body: JSON.stringify({ type: 'report_export', projectId: project.id })
            });
          } catch (telemetryErr) {
            console.error('Failed to log export telemetry:', telemetryErr);
          }
        })();
      }
    } catch (err: any) {
      console.error("PDF export failed:", err);
      const errorMessage = err?.message || "Unknown error occurred";
      setDownloadError(`Failed to generate PDF: ${errorMessage}`);
      window.alert(`CRITICAL ERROR: PDF Generation Failed\n\nDetails: ${errorMessage}\n\nPlease check the console for more information.`);
    } finally {
      setIsDownloading(false);
      if (autoDownload) onClose();
    }
  };

  useEffect(() => {
    if (autoDownload && status === 'done' && !isFlattening && !isDownloading && isAuthorized) {
      handlePrint();
    }
  }, [status, autoDownload, isFlattening, isAuthorized, isDownloading]);

  return {
    status, isDownloading, showSuccess, setShowSuccess, aiStatement,
    flattenedPlan, isFlattening, previewScale, isAuthorized, setIsAuthorized,
    showBilling, setShowBilling, isCheckingAuth, downloadError, setDownloadError,
    fixtureCounts, fittingCounts, handlePrint
  };
}
